import React, { useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage, fileUrl } from '../../lib/upload';

export function ImageUpload({ value, onChange, label = 'Photo', testId, aspect = 'square', hint }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const ratioClass = { square: 'h-24 w-24', banner: 'h-24 w-full', cover: 'h-28 w-full', avatar: 'h-20 w-20' }[aspect] || 'h-24 w-24';

  const pick = () => inputRef.current?.click();
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadImage(file);
      onChange(url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <div data-testid={testId} className="space-y-1.5">
      {label && <span className="block text-xs font-medium text-kelate-ink">{label}</span>}
      <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" data-testid={`${testId}-input`} />
      {value ? (
        <div className={`relative overflow-hidden rounded-xl border border-kelate-line bg-secondary ${ratioClass}`}>
          <img src={value ? fileUrl(value) : undefined} alt={label} className="h-full w-full object-cover" />
          <button
            type="button"
            data-testid={`${testId}-remove`}
            onClick={() => onChange('')}
            className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
            aria-label="Remove photo"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testId}-btn`}
          onClick={pick}
          disabled={busy}
          className={`flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-kelate-line bg-kelate-bg text-kelate-soft transition-colors hover:border-kelate-wood hover:text-kelate-wood disabled:opacity-50 ${ratioClass}`}
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          <span className="px-2 text-center text-[10px]">{busy ? 'Uploading...' : 'Upload'}</span>
        </button>
      )}
      {hint && <p className="text-[10px] text-kelate-soft">{hint}</p>}
    </div>
  );
}
